/**
 * Thin wrapper around a single begin/end GPU timestamp query pair. Accumulates avg/min/max over a 60-tick window
 * (~1s at 60fps) and logs on reset. Works for render and compute pass descriptors. If 'timestamp-query' is unavailable
 * every method is a silent no-op.
 *
 * @example
 *
 *   const capture = timer.available; // caller owns the capture flag
 *   if (capture) timer.inject(passDescriptor);
 *   // ... build and end the pass ...
 *   if (capture) timer.resolve(encoder);
 *   device.queue.submit([encoder.finish()]);
 *   if (capture) timer.readback();
 */
export class GpuPassTimer {

  static #WINDOW = 60;

  // GPU resources -- null when timestamp-query is unavailable.
  #querySet = null;
  #resolveBuffer = null;
  #readbackBuffer = null;
  #pending = false;
  #destroyed = false;

  #sum = 0;
  #sumSq = 0;
  #count = 0;
  #min = Infinity;
  #max = 0;

  #label;

  constructor(device, label = '') {
    this.#label = label;

    if (!device.features.has('timestamp-query')) return;

    this.#querySet = device.createQuerySet({ label: `${label}-ts`, type: 'timestamp', count: 2 });

    this.#resolveBuffer = device.createBuffer({
      label: `${label}-ts-resolve`, size: 16,
      usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC
    });
    this.#readbackBuffer = device.createBuffer({
      label: `${label}-ts-readback`, size: 16,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    });
  }

  // True when the feature is available and no readback is in flight.
  get available() {
    return this.#querySet !== null && !this.#pending;
  }

  // Injects timestampWrites into any render or compute pass descriptor.
  inject(passDescriptor) {
    passDescriptor.timestampWrites = {
      querySet: this.#querySet, beginningOfPassWriteIndex: 0, endOfPassWriteIndex: 1
    };
  }

  // Resolves the query into the readback buffer. Call on the encoder before submit.
  resolve(encoder) {
    encoder.resolveQuerySet(this.#querySet, 0, 2, this.#resolveBuffer, 0);
    encoder.copyBufferToBuffer(this.#resolveBuffer, 0, this.#readbackBuffer, 0, 16);
  }

  // Starts async readback, accumulates the result, and logs every WINDOW ticks.
  readback() {
    this.#pending = true;
    this.#readbackBuffer.mapAsync(GPUMapMode.READ).then(() => {
      if (this.#destroyed) return;

      const data = new BigUint64Array(this.#readbackBuffer.getMappedRange().slice(0));
      this.#readbackBuffer.unmap();

      const ms = Number(data[1] - data[0]) / 1e6;
      this.#sum += ms;
      this.#sumSq += ms * ms;
      this.#count++;
      if (ms < this.#min) this.#min = ms;
      if (ms > this.#max) this.#max = ms;
      this.#pending = false;

      if (this.#count >= GpuPassTimer.#WINDOW) this.log();

    }).catch(() => {this.#pending = false;});
  }

  // Logs avg ± sigma in [min, max] over the current window, then resets. Can also be called manually.
  log() {
    if (this.#count === 0) return;

    const average = this.#sum / this.#count;
    const sigma = Math.sqrt(Math.max(0, this.#sumSq / this.#count - average * average));
    const f = (v) => v.toFixed(3);

    const prefix = this.#label ? `[${this.#label}] ` : '';
    console.log(`${prefix}${f(average)} ± ${f(sigma)} in [${f(this.#min)}, ${f(this.#max)}] ms`);

    this.#sum = this.#sumSq = this.#count = 0;
    this.#min = Infinity;
    this.#max = 0;
  }

  destroy() {
    this.#destroyed = true;

    this.#querySet?.destroy();
    this.#resolveBuffer?.destroy();
    this.#readbackBuffer?.destroy();

    this.#querySet = null;
    this.#resolveBuffer = null;
    this.#readbackBuffer = null;

    this.#pending = false;

    this.#sum = this.#sumSq = this.#count = 0;
    this.#min = Infinity;
    this.#max = 0;
  }
}